import * as React from "react";
import { Link, useNavigate, useParams } from "react-router";

import { useAuth } from "@/providers";
import { PageBreadcrumbs } from "@/shared/components";
import { ModelProfileSelector } from "@/shared/components/ModelProfileSelector";
import { PageLayout } from "@/shared/components/PageLayout";
import {
  useAgent,
  useCreateAgent,
  useDeleteAgent,
  useUpdateAgent,
} from "@/shared/domain/agents/hooks";
import { useModelProfiles } from "@/shared/domain/agentThread/hooks/useModelProfiles";
import { useSkills } from "@/shared/domain/skills/hooks";
import { Button } from "@/shared/foundation/primitives";
import { Goto } from "@/shared/navigation";
import { useRegisterViewingContext } from "@/shared/services";

import { AgentDetailTabBar } from "./AgentDetailTabBar";
import styles from "./AgentEditor.module.css";
import {
  buildRequestTemplate,
  buildVariables,
  type FormState,
  type GoalState,
  overlayDraftOnForm,
  parseSavedAgent,
  type SeededFrom,
  shouldSeedForm,
  type SkillSelection,
} from "./buildRequestTemplate";
import { Collapsible } from "./Collapsible";
import { AddGoalMenu } from "./goals/AddGoalMenu";
import { GoalCard } from "./goals/GoalCard";
import { allGoalsValid } from "./goals/validation";
import { AddSkillMenu } from "./skills/AddSkillMenu";
import { SkillRow } from "./skills/SkillRow";
import { isAgentEligibleSkill, validateSkills } from "./skills/validation";
import { useDraftSeededForm } from "./useDraftSeededForm";
import { VariablesPanel } from "./VariablesPanel";

const MAX_GOALS_PER_TURN = 5;

interface AgentEditorProps {
  /** ``create`` is the new-agent route; ``edit`` mounts at
   *  ``/agents/:agentId``. We accept this as a prop rather than inferring
   *  from ``useParams`` so the same component renders cleanly during the
   *  create→edit transition after a successful POST, before React Router
   *  has remounted with the new id. */
  mode: "create" | "edit";
}

function newGoal(goal_type: GoalState["goal_type"]): GoalState {
  if (goal_type === "dataset_summary") {
    return { goal_type, summary_format_spec_prompt: "" };
  }
  if (goal_type === "create_events") {
    return {
      goal_type,
      event_vocabulary: [{ name: "", description: "" }],
      tag_vocabulary: [],
      collection_enabled: false,
      event_focus_prompt: "",
    };
  }
  return {
    goal_type: "dataset_triage",
    label_vocabulary: [{ label: "", description: "" }],
  };
}

export function AgentEditor({ mode }: AgentEditorProps) {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId: string }>();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const agentQuery = useAgent(mode === "edit" ? agentId : undefined, {
    orgId,
  });

  useRegisterViewingContext("agent", { orgId: agentQuery.data?.org_id });
  const profilesQuery = useModelProfiles();
  // No scope filter: this returns every visible skill, which we then narrow
  // to the org-shared tiers (``org`` and ``org-editable``). The "org" scope
  // would wrongly exclude org-shared skills the author wrote themselves.
  const orgSkillsQuery = useSkills(orgId);

  const createMutation = useCreateAgent();
  const updateMutation = useUpdateAgent();
  const deleteMutation = useDeleteAgent();

  // In edit mode `pendingUpdateDraft` is the AI's proposed changes for THIS
  // agent (claimed only when a stashed draft's `target_agent_id` matches the
  // routed id); it is overlaid onto the fetched agent by the seed effect
  // below. In create mode it is always null.
  const [form, setForm, pendingUpdateDraft] = useDraftSeededForm(mode, agentId);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  // Read-only by default in edit mode; the Edit button flips this. Create
  // mode is always "editing" because there's no saved state to view. An AI
  // update handoff also opens directly in editing mode so the prefilled
  // proposal is immediately reviewable and saveable — otherwise it would sit
  // in the read-only view and a Cancel would discard the AI's changes before
  // the user could act on them.
  const [isEditing, setIsEditing] = React.useState<boolean>(
    mode === "create" || pendingUpdateDraft !== null,
  );
  // The initial value above covers a handoff that lands on a fresh mount. A
  // handoff can also arrive while this editor is already open — React Router
  // reuses the element across `/agents/:agentId` navigations — in which case
  // the draft is claimed on a later render and the initial value has already
  // committed as `false`, leaving the proposal stranded in the read-only view.
  React.useEffect(() => {
    if (pendingUpdateDraft !== null) {
      setIsEditing(true);
    }
  }, [pendingUpdateDraft]);

  // Seed once when the agent lands (edit mode only). A later refetch
  // (e.g. after a successful update mutation invalidates the cache)
  // must not clobber unsaved local edits, so we gate seeding on the
  // agent_id rather than the data identity.
  //
  // On the AI update handoff, `pendingUpdateDraft` carries the proposed
  // fields: we seed from the saved agent and then overlay the draft over
  // it (provided fields replace, omitted fields keep the saved values;
  // goals / variables replace the whole list when provided).
  //
  // The gate is (agent_id, claimed draft), not agent_id alone — see
  // {@link shouldSeedForm} for why the proposal has to be part of it.
  const seededRef = React.useRef<SeededFrom | null>(null);
  React.useEffect(() => {
    if (mode !== "edit") {
      return;
    }
    const agent = agentQuery.data;
    if (agent === undefined) {
      return;
    }
    if (
      !shouldSeedForm(seededRef.current, agent.agent_id, pendingUpdateDraft)
    ) {
      return;
    }
    seededRef.current = { agentId: agent.agent_id, draft: pendingUpdateDraft };
    // An empty draft overlays to the saved form unchanged, so the manual
    // and AI-handoff paths share one expression.
    setForm(
      overlayDraftOnForm(parseSavedAgent(agent), pendingUpdateDraft ?? {}),
    );
    // `setForm` is the stable state setter returned by `useDraftSeededForm`'s
    // useState. Listed for the lint rule, which can't infer stability across
    // the hook boundary. `pendingUpdateDraft` is claimed during render and
    // stays reference-stable for as long as the claim is held, so it is
    // already the draft for this `agent_id` by the time this effect sees the
    // agent, and a later proposal arrives as a new reference the gate above
    // reads as "not yet seeded".
  }, [mode, agentQuery.data, setForm, pendingUpdateDraft]);

  const variables = React.useMemo(() => buildVariables(form), [form]);

  const hasGoalOrKickoff =
    form.goals.length > 0 || form.kickoffMessage.trim().length > 0;
  const goalsWithinCap = form.goals.length <= MAX_GOALS_PER_TURN;
  const goalsValid = allGoalsValid(form.goals);

  // Every visible skill, narrowed to the org-shared tiers — the only kinds an
  // agent may attach, since a private skill would not resolve for other org
  // members who invoke the agent. ``org`` and ``org-editable`` are both
  // visible org-wide; only their edit surface differs.
  const eligibleSkills = React.useMemo(
    () => (orgSkillsQuery.data?.items ?? []).filter(isAgentEligibleSkill),
    [orgSkillsQuery.data],
  );
  const skillById = React.useMemo(
    () => new Map(eligibleSkills.map((s) => [s.skill.skill_id, s])),
    [eligibleSkills],
  );
  const selectedSkillIds = new Set(form.skills.map((s) => s.skill_id));
  const selectableSkills = eligibleSkills.filter(
    (s) => !selectedSkillIds.has(s.skill.skill_id),
  );
  // Validate selected skills only once the org skills query has settled — an
  // unsettled (still empty) list would flag every attached skill as missing.
  const skillsValid =
    !orgSkillsQuery.isSuccess ||
    validateSkills(form.skills, eligibleSkills).length === 0;

  const canSubmit =
    orgId !== undefined &&
    form.name.trim().length > 0 &&
    hasGoalOrKickoff &&
    goalsWithinCap &&
    goalsValid &&
    skillsValid &&
    !createMutation.isPending &&
    !updateMutation.isPending;

  const updateForm = <K extends keyof FormState>(
    key: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateGoalAt = (idx: number, next: GoalState) => {
    setForm((prev) => ({
      ...prev,
      goals: prev.goals.map((g, i) => (i === idx ? next : g)),
    }));
  };

  const removeGoalAt = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== idx),
    }));
  };

  const addGoal = (goal_type: GoalState["goal_type"]) => {
    setForm((prev) => ({
      ...prev,
      goals: [...prev.goals, newGoal(goal_type)],
    }));
  };

  const updateSkillAt = (idx: number, next: SkillSelection) => {
    setForm((prev) => ({
      ...prev,
      skills: prev.skills.map((s, i) => (i === idx ? next : s)),
    }));
  };

  const removeSkillAt = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== idx),
    }));
  };

  // New skills are added unpinned (version null -> track latest); the row's
  // version selector lets the author pin afterwards.
  const addSkill = (skillId: string) => {
    setForm((prev) => ({
      ...prev,
      skills: [...prev.skills, { skill_id: skillId, version: null }],
    }));
  };

  const updateVariableMeta = React.useCallback(
    (
      name: string,
      patch: Partial<{ description: string; default: string }>,
    ) => {
      setForm((prev) => {
        const current = prev.variableMeta[name] ?? {
          description: "",
          default: "",
        };
        return {
          ...prev,
          variableMeta: {
            ...prev.variableMeta,
            [name]: { ...current, ...patch },
          },
        };
      });
    },
    // `setForm` is stable; listed for the lint rule, which can't infer
    // stability across the `useDraftSeededForm` hook boundary.
    [setForm],
  );

  const handleSubmit = async () => {
    if (!orgId || !canSubmit) {
      return;
    }
    setSubmitError(null);
    const requestBody = {
      name: form.name.trim(),
      description:
        form.description.trim() === "" ? null : form.description.trim(),
      request_template: buildRequestTemplate(form),
      variables,
    };
    try {
      if (mode === "create") {
        const created = await createMutation.mutateAsync({
          orgId,
          request: requestBody,
        });
        void navigate(Goto.createAgentRoute(created.agent_id));
      } else if (agentId !== undefined) {
        await updateMutation.mutateAsync({
          orgId,
          agentId,
          request: {
            name: requestBody.name,
            description: requestBody.description,
            request_template: requestBody.request_template,
            variables: requestBody.variables,
          },
        });
        // Successful save returns the page to read-only. The mutation
        // already invalidated the cache, so a subsequent Edit→Cancel
        // would revert against the just-persisted state.
        setIsEditing(false);
      }
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Save failed; please retry.",
      );
    }
  };

  const handleCancel = () => {
    if (mode === "create") {
      // Nothing to revert to — back out of the new-agent route entirely.
      void navigate("/agents");
      return;
    }
    // Edit mode: discard local edits by re-seeding from the cached agent,
    // clear any save-time error, and drop back to read-only.
    if (agentQuery.data !== undefined) {
      setForm(parseSavedAgent(agentQuery.data));
    }
    setSubmitError(null);
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!orgId || agentId === undefined) {
      return;
    }
    if (!window.confirm("Delete this agent? This cannot be undone.")) {
      return;
    }
    try {
      await deleteMutation.mutateAsync({ orgId, agentId });
      void navigate("/agents");
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Delete failed; please retry.",
      );
    }
  };

  const headerTitle = mode === "create" ? "Create Agent" : form.name || "Agent";
  const pageTitle = `Roboto - ${headerTitle}`;
  // Details | Threads strip linking the agent-detail pages. Edit mode
  // only — a not-yet-created agent has no threads page to tab to.
  // Rendered in every edit-mode branch (loaded, loading, error) so the
  // chrome stays stable and the user can tab away from a failed load;
  // it must sit *inside* ``.container`` because the container's
  // negative-margin background bleed would overlap a preceding sibling.
  const detailTabBar = mode === "edit" && agentId !== undefined && (
    <AgentDetailTabBar agentId={agentId} />
  );
  const headerContentLeft = (
    <PageBreadcrumbs>
      <Link to="/agents">Agents</Link>
      <span>{headerTitle}</span>
    </PageBreadcrumbs>
  );
  // Three button arrangements depending on (mode, isEditing):
  //   create               → [Cancel] [Create]            (no Launch; isEditing always true)
  //   edit + !isEditing    → [Launch] [Edit]              (read-only view)
  //   edit + isEditing     → [Launch] [Cancel] [Save]     (the active write surface)
  // Launch is always shown in the loaded edit view (the loading and
  // error branches render no header actions) so a teammate can run the
  // saved agent without having to leave edit mode first.
  const headerActions = (
    <div className={styles.actions}>
      {mode === "edit" && agentId !== undefined && (
        <Button
          variant="outlined"
          onClick={() => {
            void navigate(Goto.createAgentLaunchRoute(agentId));
          }}
        >
          Launch
        </Button>
      )}
      {mode === "edit" && !isEditing && (
        <Button variant="contained" onClick={() => setIsEditing(true)}>
          Edit
        </Button>
      )}
      {(mode === "create" || isEditing) && (
        <>
          <Button variant="text" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit}
            onClick={() => {
              void handleSubmit();
            }}
          >
            {mode === "create" ? "Create" : "Save changes"}
          </Button>
        </>
      )}
    </div>
  );

  if (mode === "edit" && agentQuery.isError) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.container}>
          {detailTabBar}
          <div className={styles.error}>
            Failed to load agent. {agentQuery.error?.message ?? ""}
          </div>
        </div>
      </PageLayout>
    );
  }

  // ``data === undefined`` also catches the disabled-query window:
  // TanStack v5 reports ``isLoading: false`` for a disabled query, and
  // ``useAgent`` is disabled until ``orgId`` resolves — without the extra
  // term, that window falls through and flashes an unseeded read-only
  // form. Same guard idiom as ``LaunchAgent`` and ``AgentThreadsPage``.
  if (
    mode === "edit" &&
    (agentQuery.isLoading || agentQuery.data === undefined)
  ) {
    return (
      <PageLayout title={pageTitle} headerContentLeft={headerContentLeft}>
        <div className={styles.container}>
          {detailTabBar}
          <div>Loading agent&hellip;</div>
        </div>
      </PageLayout>
    );
  }

  const profiles = profilesQuery.data ?? [];
  const defaultProfileId =
    profiles.find((p) => p.is_default)?.id ?? profiles[0]?.id ?? "";
  const modelProfileValue = form.modelProfile ?? defaultProfileId;

  return (
    <PageLayout
      title={pageTitle}
      headerContentLeft={headerContentLeft}
      headerContentRight={headerActions}
    >
      <div className={styles.container}>
        {detailTabBar}
        {/* The fieldset is the read-only/editable gate: when disabled, the
            browser turns off every interactive descendant (inputs,
            textareas, buttons, the model-profile select), so each form
            component does not need its own readOnly prop. ``form="agent-
            editor"`` is unset because the buttons that submit live outside
            the fieldset (in the page header), so they remain enabled. */}
        <fieldset className={styles.fieldset} disabled={!isEditing}>
          {/* Name, description, model, and kickoff are the entry-surface
              fields every author touches. They run flat at the top of the
              form: no section header (the field labels already say what
              each row is) and no framed card (chevron-to-open friction
              isn't worth it for four always-shown fields). The framed-
              card pattern resumes below for Goals, Skills, and Variables,
              which each carry repeated/structured sub-content that
              benefits from visual separation. */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="agent-name">
              Name
              {/* The asterisk is purely a visual affordance — the input's
                  ``required`` attribute is what AT relies on. ``aria-
                  hidden`` keeps the screen-reader announcement to just
                  "Name, required" rather than "Name star, required". */}
              <span aria-hidden="true" className={styles.required}>
                *
              </span>
            </label>
            <p className={styles.help}>
              How teammates will find and recognize this agent.
            </p>
            <input
              id="agent-name"
              className={styles.input}
              value={form.name}
              onChange={(e) => updateForm("name", e.target.value)}
              placeholder="e.g. Triage anomalous drives"
              required
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="agent-description">
              Description
              <span className={styles.optional}>(optional)</span>
            </label>
            <p className={styles.help}>
              What does this agent do, and when should teammates invoke it?
            </p>
            <textarea
              id="agent-description"
              className={styles.input}
              rows={3}
              value={form.description}
              onChange={(e) => updateForm("description", e.target.value)}
              placeholder="Enter a description"
            />
          </div>
          <div className={styles.field}>
            <span className={styles.label}>Model</span>
            <p className={styles.help}>
              Choose the model that best balances speed, cost, and reasoning
              depth.
            </p>
            {profilesQuery.isLoading ? (
              <span className={styles.muted}>
                Loading model profiles&hellip;
              </span>
            ) : (
              <ModelProfileSelector
                profiles={profiles}
                value={modelProfileValue}
                onChange={(profileId) => updateForm("modelProfile", profileId)}
                // Inline segmented pills with a description line below;
                // there's room for it on the editor page and a one-click
                // model swap reads more clearly than a popover.
                variant="segmented"
              />
            )}
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="agent-kickoff">
              Kickoff message
              <span className={styles.optional}>(optional)</span>
            </label>
            <p className={styles.help}>
              Seeds the conversation with a user message before the
              agent&rsquo;s first turn. Use this for context the model needs but
              that doesn&rsquo;t fit a goal &mdash; leave blank to let the
              server synthesize one. Reference variables with{" "}
              <code>{"{{name}}"}</code>.
            </p>
            <textarea
              id="agent-kickoff"
              className={styles.input}
              rows={4}
              value={form.kickoffMessage}
              onChange={(e) => updateForm("kickoffMessage", e.target.value)}
              placeholder="Focus the triage on the {{customer}} workspace this week."
            />
          </div>

          <Collapsible
            title="Goals"
            subtitle="What the agent must accomplish before its turn is considered done."
            headerActions={
              // Just the running count. ``MAX_GOALS_PER_TURN`` is a real
              // backend cap (wire validation in
              // ``SendMessageRequest.goals``) but is unreachable through
              // normal use today — two goal types and no reason to
              // declare duplicates of either — so advertising "/ N"
              // implies a constraint the user won't hit. The
              // ``AddGoalMenu`` below still disables at the cap, so the
              // guardrail stays intact.
              <span className={styles.goalsCount}>{form.goals.length}</span>
            }
          >
            {form.goals.length === 0 ? (
              <div className={styles.goalsEmpty}>
                <p className={styles.muted}>
                  No goals yet. Add one to anchor the agent&rsquo;s work &mdash;
                  without a goal, the agent will just have a conversation.
                </p>
                {isEditing && <AddGoalMenu onAdd={addGoal} />}
              </div>
            ) : (
              <>
                <div className={styles.goalsList}>
                  {form.goals.map((goal, idx) => (
                    <GoalCard
                      key={idx}
                      goal={goal}
                      index={idx}
                      isEditing={isEditing}
                      onChange={(next) => updateGoalAt(idx, next)}
                      onRemove={() => removeGoalAt(idx)}
                    />
                  ))}
                </div>
                {isEditing && (
                  <AddGoalMenu
                    onAdd={addGoal}
                    disabled={form.goals.length >= MAX_GOALS_PER_TURN}
                  />
                )}
              </>
            )}
          </Collapsible>

          <Collapsible
            title="Skills"
            subtitle="Org-shared skills this agent's AI can invoke. Pin a version, or leave on Latest to track the newest."
            headerActions={
              <span className={styles.goalsCount}>{form.skills.length}</span>
            }
          >
            {orgSkillsQuery.isError && (
              <p className={styles.muted}>
                Failed to load skills. {orgSkillsQuery.error?.message ?? ""}
              </p>
            )}
            {!orgSkillsQuery.isError &&
              (form.skills.length === 0 ? (
                <div className={styles.goalsEmpty}>
                  <p className={styles.muted}>
                    No skills attached &mdash; the agent&rsquo;s AI will have no
                    skills to invoke.
                  </p>
                  {isEditing && (
                    <AddSkillMenu
                      options={selectableSkills}
                      loading={orgSkillsQuery.isLoading}
                      onAdd={addSkill}
                    />
                  )}
                </div>
              ) : (
                <>
                  <div className={styles.goalsList}>
                    {form.skills.map((skill, idx) => (
                      <SkillRow
                        key={idx}
                        selection={skill}
                        summary={skillById.get(skill.skill_id)}
                        isEditing={isEditing}
                        onChange={(next) => updateSkillAt(idx, next)}
                        onRemove={() => removeSkillAt(idx)}
                      />
                    ))}
                  </div>
                  {isEditing && (
                    <AddSkillMenu
                      options={selectableSkills}
                      loading={orgSkillsQuery.isLoading}
                      onAdd={addSkill}
                    />
                  )}
                </>
              ))}
          </Collapsible>

          <Collapsible
            title="Variables"
            subtitle={
              <>
                Auto-detected from <code>{"{{name}}"}</code> placeholders in
                fields above. Edit a placeholder to rename or remove its
                variable.
              </>
            }
            headerActions={
              <span className={styles.goalsCount}>{variables.length}</span>
            }
          >
            <VariablesPanel
              variables={variables}
              variableMeta={form.variableMeta}
              onMetaChange={updateVariableMeta}
            />
          </Collapsible>

          {!hasGoalOrKickoff && form.name.trim().length > 0 && (
            <div className={styles.warning}>
              Add at least one goal or a kickoff message before saving — an
              agent needs something to do.
            </div>
          )}
          {submitError && <div className={styles.error}>{submitError}</div>}

          {mode === "edit" && isEditing && agentId !== undefined && (
            <Collapsible title="Danger zone" defaultOpen={false}>
              <div>
                <Button
                  variant="outlined"
                  color="error"
                  disabled={deleteMutation.isPending}
                  onClick={() => {
                    void handleDelete();
                  }}
                >
                  {deleteMutation.isPending ? "Deleting…" : "Delete agent"}
                </Button>
              </div>
            </Collapsible>
          )}
        </fieldset>
      </div>
    </PageLayout>
  );
}
